import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaPlay } from 'react-icons/fa';
import {
  SiHtml5, SiCss3, SiJavascript, SiReact, SiNodedotjs,
  SiNextdotjs, SiTypescript, SiTailwindcss, SiMongodb, SiGit, SiRedux
} from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';
import './Hero.css';

const Hero = () => {
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  // 🛰️ Orbiting tech stack icons
  const innerOrbitTechs = [
    { icon: <SiHtml5 />, color: '#E34F26' },
    { icon: <SiCss3 />, color: '#1572B6' },
    { icon: <SiJavascript />, color: '#D8B70A' },
    { icon: <SiReact />, color: '#61DAFB' },
    { icon: <SiNodedotjs />, color: '#339933' }
  ];

  const outerOrbitTechs = [
    { icon: <SiNextdotjs />, color: '#111111' },
    { icon: <SiTypescript />, color: '#3178C6' },
    { icon: <SiTailwindcss />, color: '#06B6D4' },
    { icon: <SiMongodb />, color: '#47A248' },
    { icon: <SiGit />, color: '#F05032' },
    { icon: <SiRedux />, color: '#764ABC' }
  ];

  // Seconds each icon gets before the energy flow moves on to the next one
  const orbitSlotSeconds = 1;

  // Spark burst fired the moment the wire touches an icon - each one is a
  // (dx, dy) landing offset in px, biased downward like falling cracker sparks.
  const orbitSparkOffsets = [
    { dx: 37, dy: 10, color: 'var(--secondary)' },
    { dx: 22, dy: 31, color: 'var(--accent)' },
    { dx: 0, dy: 38, color: 'var(--secondary)' },
    { dx: -22, dy: 31, color: 'var(--accent)' },
    { dx: -37, dy: 10, color: 'var(--secondary)' }
  ];

  const renderOrbitSparks = (delay) =>
    orbitSparkOffsets.map((spark, i) => (
      <span
        key={i}
        className="orbit-spark"
        style={{
          '--dx': `${spark.dx}px`,
          '--dy': `${spark.dy}px`,
          '--delay': delay,
          background: spark.color,
          color: spark.color
        }}
      ></span>
    ));

  // 🔥 Tabs Data
  const tabsData = [
    { name: "Web Development", icon: "💻" },
    { name: "Mobile Apps", icon: "📱" },
    { name: "Cloud Services", icon: "☁️" },
    { name: "UI/UX Design", icon: "🎨" },
    { name: "Fullstack", icon: "⚡" }
  ];

  const [activeTab, setActiveTab] = useState(2);

  // 🔄 Auto switch tabs
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % tabsData.length);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="hero">
      <div className="hero-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="container hero-container">
        {/* LEFT SIDE */}
        <div className="hero-content">
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-badge"
          >
            <span className="badge-text">
              🚀 Software Development Company in India
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-title"
          >
            Software Development Company in India for <br />

            <motion.span 
              className="gradient-text typing-wrapper"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.5 }}
            >
              <TypeAnimation
                sequence={[
                  '𝒘𝒆𝒃 𝒔𝒐𝒍𝒖𝒕𝒊𝒐𝒏𝒔',
                  2000,
                  '𝐌𝐨𝐛𝐢𝐥𝐞 𝐀𝐩𝐩𝐬',
                  2000,
                  '𝑨𝑰 𝑺𝒐𝒍𝒖𝒕𝒊𝒐𝒏𝒔',
                  2000,
                  '𝑪𝒍𝒐𝒖𝒅 𝑺𝒚𝒔𝒕𝒆𝒎𝒔',
                  2000,
                  '𝑫𝒊𝒈𝒊𝒕𝒂𝒍 𝑻𝒓𝒂𝒏𝒔𝒇𝒐𝒓𝒎𝒂𝒕𝒊𝒐𝒏',
                  2000,
                ]}
                wrapper="span"
                speed={50}
                repeat={Infinity}
                className="typing-text"
                cursor={true}
              />
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hero-description"
          >
            ZustandTech helps startups and businesses build scalable web applications,
            mobile apps, AI solutions, and cloud systems.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hero-buttons"
          >
            <Link to="/contact" className="btn btn-primary">
              Get Started <FaArrowRight />
            </Link>

            <Link to="/watch-demo" className="btn btn-outline">
  <FaPlay /> Watch Demo
</Link>
          </motion.div>
        </div>

        {/* RIGHT SIDE - Code Window and Image */}
        <div className="hero-right">
          {/* Image with service badges - Positioned below/near the code window */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="hero-image"
          >
            <div className="hero-image-wrapper">
              <div className="tech-orbit" aria-hidden="true">
                <span className="orbit-path orbit-path-1"></span>
                <span className="orbit-path orbit-path-2"></span>

                <div className="orbit-ring orbit-ring-1">
                  {innerOrbitTechs.map((tech, index) => {
                    const angle = `${(360 / innerOrbitTechs.length) * index}deg`;
                    const delay = `${index * orbitSlotSeconds}s`;
                    return (
                      <React.Fragment key={`inner-${index}`}>
                        <span className="orbit-wire" style={{ '--angle': angle, '--delay': delay }}></span>
                        <div className="orbit-item" style={{ '--angle': angle }}>
                          <div className="orbit-icon-wrap">
                            <span className="orbit-icon" style={{ color: tech.color, '--delay': delay }}>
                              {tech.icon}
                            </span>
                            {renderOrbitSparks(delay)}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="orbit-ring orbit-ring-2">
                  {outerOrbitTechs.map((tech, index) => {
                    const angle = `${(360 / outerOrbitTechs.length) * index}deg`;
                    const delay = `${(innerOrbitTechs.length + index) * orbitSlotSeconds}s`;
                    return (
                      <React.Fragment key={`outer-${index}`}>
                        <span className="orbit-wire" style={{ '--angle': angle, '--delay': delay }}></span>
                        <div className="orbit-item" style={{ '--angle': angle }}>
                          <div className="orbit-icon-wrap">
                            <span className="orbit-icon" style={{ color: tech.color, '--delay': delay }}>
                              {tech.icon}
                            </span>
                            {renderOrbitSparks(delay)}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="orbit-core-wrap">
                  <span className="orbit-core-halo"></span>
                  <div className="orbit-core">
                    <div className="orbit-core-face orbit-core-front">
                      <img src="/ar.png" alt="ZustandTech logo" className="orbit-core-logo" />
                    </div>
                    <div className="orbit-core-face orbit-core-back">
                      <img src="/ar.png" alt="" className="orbit-core-logo" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;